import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [realName, setRealName] = useState('');
  const [nickname, setNickname] = useState('');
  const [displayNameType, setDisplayNameType] = useState<'real' | 'nickname'>('nickname');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      await register({
        email,
        password,
        realName,
        nickname,
        displayNameType
      });
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string; errors?: Array<{ msg: string }> } } };
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.errors?.[0]?.msg ||
                          'Registration failed. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src="/images/dartsScheduler-trn.png" alt="Darts Scheduler" className="h-12 mx-auto mb-4" />
          </Link>
          <h1 className="text-4xl font-bold text-white mb-1">Dart Scheduler</h1>
          <div className="text-[#1111d4] text-sm font-medium tracking-wider uppercase">Multi-User Edition</div>
          <p className="text-gray-500 mt-4">Create your account</p>
        </div>

        <div className="card bg-[#0d0d1a]/80 backdrop-blur-sm border-[#1a1a3a]">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg text-sm border border-red-800">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="realName" className="label">Full Name</label>
              <input
                type="text"
                id="realName"
                value={realName}
                onChange={(e) => setRealName(e.target.value)}
                className="input"
                placeholder="Your real name"
                required
                maxLength={100}
              />
            </div>

            <div>
              <label htmlFor="nickname" className="label">Nickname</label>
              <input
                type="text"
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="input"
                placeholder="How you want to be known"
                required
                maxLength={100}
              />
            </div>

            <div>
              <label className="label">Public Display Name</label>
              <p className="text-xs text-gray-500 mb-2">
                Choose which name appears on your public profile and banner
              </p>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="displayNameType"
                    value="nickname"
                    checked={displayNameType === 'nickname'}
                    onChange={() => setDisplayNameType('nickname')}
                    className="w-4 h-4 text-[#1111d4] bg-[#1a1a3a] border-[#2a2a4a] focus:ring-[#1111d4]"
                  />
                  <span className="text-gray-300">
                    Nickname {nickname && <span className="text-gray-500">({nickname})</span>}
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="displayNameType"
                    value="real"
                    checked={displayNameType === 'real'}
                    onChange={() => setDisplayNameType('real')}
                    className="w-4 h-4 text-[#1111d4] bg-[#1a1a3a] border-[#2a2a4a] focus:ring-[#1111d4]"
                  />
                  <span className="text-gray-300">
                    Full Name {realName && <span className="text-gray-500">({realName})</span>}
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="label">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                required
                minLength={8}
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500 mt-1">
                Must be at least 8 characters
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                required
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-4 text-center text-sm text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-[#1111d4] hover:text-[#1414f0] hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
